/**
 * Compute health_code for menu items from customer allergy/dislike IDs.
 * 0 = no conflict, 1 = optional allergy, 2 = optional dislike, 10 = mandatory allergy, 20 = mandatory dislike.
 *
 * Matching uses allergy_id / dislike_id / inventory_id only (string or number).
 * Recipe/ingredient row `id` is a different namespace and must not be compared to inventory prefs
 * (ID collisions were hiding unrelated menu items for combined/Foodna clients).
 *
 * Combined mode may pass the full inventory pool as allergy_ids (dislike_ids empty).
 * Cross-field: a selected allergy id still matches recipe `dislike_id` tags for *warnings*,
 * but mandatory elevation / hide only uses allergy_id + inventory_id on a mandatory node.
 */
import type { MenuBuilder } from './types';

function parseNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function idListHasMatch(raw: unknown, selected: Set<number>): boolean {
  if (Array.isArray(raw)) {
    return raw.some(entry => {
      const id = parseNumericId(entry);
      return id != null && selected.has(id);
    });
  }
  const id = parseNumericId(raw);
  return id != null && selected.has(id);
}

/** Inventory / allergy id fields — same namespace as preference inventory ids. */
function nodeMatchesAllergyInventoryFields(node: any, selected: Set<number>): boolean {
  if (!node || selected.size === 0) {
    return false;
  }
  if (idListHasMatch(node.allergy_id ?? node.allergy_id_list, selected)) {
    return true;
  }
  return idListHasMatch(node.inventory_id ?? node.inventory_id_list, selected);
}

/** Dislike id fields (+ inventory) — for dislike classification. */
function nodeMatchesDislikeInventoryFields(node: any, selected: Set<number>): boolean {
  if (!node || selected.size === 0) {
    return false;
  }
  if (idListHasMatch(node.dislike_id ?? node.dislike_id_list, selected)) {
    return true;
  }
  return idListHasMatch(node.inventory_id ?? node.inventory_id_list, selected);
}

/**
 * Allergy pool match for warnings: allergy_id / inventory_id, plus dislike_id tags
 * (combined inventory is often only tagged as dislike_id on recipes).
 */
function nodeMatchesAllergyWarningFields(node: any, selected: Set<number>): boolean {
  if (nodeMatchesAllergyInventoryFields(node, selected)) {
    return true;
  }
  return idListHasMatch(node?.dislike_id ?? node?.dislike_id_list, selected);
}

function isRecipeNodeMandatory(node: any): boolean {
  return (
    node?.is_mandatory === true ||
    node?.mandatory === true ||
    node?.is_mandatory_for_menu_item === 1 ||
    node?.is_mandatory_for_menu_item === true
  );
}

/**
 * Walk recipe tree. Mandatory only elevates when THIS node both matches (strict fields)
 * and is mandatory — dislike_id-only matches warn but never elevate to hide codes.
 */
function collectIdsFromRecipeTree(
  node: any,
  allergy_ids: Set<number>,
  dislike_ids: Set<number>,
  out: {
    has_allergy: boolean;
    has_dislike: boolean;
    mandatory_allergy: boolean;
    mandatory_dislike: boolean;
  },
) {
  if (!node) {
    return;
  }

  const matched_allergy_warn = nodeMatchesAllergyWarningFields(node, allergy_ids);
  const matched_dislike = nodeMatchesDislikeInventoryFields(node, dislike_ids);
  if (matched_allergy_warn) {
    out.has_allergy = true;
  }
  if (matched_dislike) {
    out.has_dislike = true;
  }
  if (isRecipeNodeMandatory(node) && nodeMatchesAllergyInventoryFields(node, allergy_ids)) {
    out.mandatory_allergy = true;
  }
  if (isRecipeNodeMandatory(node) && nodeMatchesDislikeInventoryFields(node, dislike_ids)) {
    out.mandatory_dislike = true;
  }

  const children = node.recipes ?? node.children ?? node.ingredients;
  if (Array.isArray(children)) {
    children.forEach((child: any) => collectIdsFromRecipeTree(child, allergy_ids, dislike_ids, out));
  }
}

function itemIdsMatch(raw_ids: unknown, selected: Set<number>): boolean {
  return Array.isArray(raw_ids) && idListHasMatch(raw_ids, selected);
}

function getHealthCodeForItem(item: any, allergy_ids: number[], dislike_ids: number[]): number {
  // Always recompute from current allergy/dislike IDs.
  // Trusting a prior `health_code` (including 0 written on first enrich before prefs load)
  // permanently poisons later passes after setMenuData writes enriched builders back into the slot.
  const a_set = new Set(allergy_ids.filter(id => Number.isFinite(id)));
  const d_set = new Set(dislike_ids.filter(id => Number.isFinite(id)));

  const out = {
    has_allergy: false,
    has_dislike: false,
    mandatory_allergy: false,
    mandatory_dislike: false,
  };
  const recipes = item?.recipes;
  if (Array.isArray(recipes)) {
    recipes.forEach((recipe: any) => collectIdsFromRecipeTree(recipe, a_set, d_set, out));
  }
  if (out.has_allergy) {
    return out.mandatory_allergy ? 10 : 1;
  }
  if (out.has_dislike) {
    return out.mandatory_dislike ? 20 : 2;
  }

  // Item-level id lists have no mandatory flag — warn only (optional codes).
  // Treating these as 10/20 was hiding dishes whose allergen appears only as an optional ingredient.
  const direct_allergy =
    itemIdsMatch(item?.allergy_ids, a_set) || itemIdsMatch(item?.inventory_ids, a_set);
  const direct_dislike =
    itemIdsMatch(item?.dislike_ids, d_set) || itemIdsMatch(item?.inventory_ids, d_set);
  if (direct_allergy) {
    return 1;
  }
  if (direct_dislike) {
    return 2;
  }
  return 0;
}

export function enrichMenuBuildersWithHealthCode(
  menu_builders: MenuBuilder[],
  allergy_ids: number[],
  dislike_ids: number[],
): MenuBuilder[] {
  return menu_builders.map(builder => {
    const type_data = (builder.type_data ?? []).map(it => {
      const code = getHealthCodeForItem(it, allergy_ids, dislike_ids);
      const repl = (it as any).replaceable_menu_items ?? [];
      const repl_enriched = repl.map((r: any) => ({
        ...r,
        health_code: getHealthCodeForItem(r, allergy_ids, dislike_ids),
      }));
      return { ...it, health_code: code, replaceable_menu_items: repl_enriched };
    });
    return { ...builder, type_data };
  });
}

/** True when a mandatory recipe/ingredient node matches via allergy_id or inventory_id. */
function recipeTreeHasMandatoryAllergyMatch(node: any, allergy_ids: Set<number>): boolean {
  if (!node) {
    return false;
  }
  if (isRecipeNodeMandatory(node) && nodeMatchesAllergyInventoryFields(node, allergy_ids)) {
    return true;
  }
  const children = node.recipes ?? node.children ?? node.ingredients;
  if (!Array.isArray(children)) {
    return false;
  }
  return children.some((child: any) => recipeTreeHasMandatoryAllergyMatch(child, allergy_ids));
}

function menuItemIsPinnedVisible(item: any): boolean {
  return item?.is_selected === true || Number(item?.quantity ?? 0) > 0;
}

/**
 * Combined allergy mode: hide items that conflict with a *mandatory* allergen inventory,
 * unless the API already assigned/selected them (`is_selected` or `quantity > 0`).
 * Optional allergy conflicts (health_code 1) stay visible with the normal health warning.
 */
export function itemHasMandatoryAllergyConflict(item: any, allergy_ids: number[]): boolean {
  if (!item || allergy_ids.length === 0) {
    return false;
  }
  if (typeof item.health_code === 'number' && item.health_code === 10) {
    return true;
  }
  const a_set = new Set(allergy_ids.filter(id => Number.isFinite(id)));
  const recipes = item.recipes;
  if (Array.isArray(recipes)) {
    return recipes.some((r: any) => recipeTreeHasMandatoryAllergyMatch(r, a_set));
  }
  return false;
}

export function filterMenuBuildersHidingMandatoryAllergyConflicts(
  menu_builders: MenuBuilder[],
  allergy_ids: number[],
): MenuBuilder[] {
  if (!allergy_ids.length || menu_builders.length === 0) {
    return menu_builders;
  }
  return menu_builders.map(builder => ({
    ...builder,
    type_data: (builder.type_data ?? []).filter(it => {
      if (menuItemIsPinnedVisible(it)) {
        return true;
      }
      return !itemHasMandatoryAllergyConflict(it, allergy_ids);
    }),
  }));
}

/**
 * Enrich health codes, then optionally drop mandatory allergy conflicts from the list.
 * Prefer enrich-only into the menu store and filter at display time so hidden items can
 * reappear when preferences change (filtered lists written back to the slot stick forever).
 */
export function prepareMenuBuildersWithHealthPolicy(
  menu_builders: MenuBuilder[],
  allergy_ids: number[],
  dislike_ids: number[],
  options?: { hide_mandatory_allergy_items?: boolean },
): MenuBuilder[] {
  const enriched = enrichMenuBuildersWithHealthCode(menu_builders, allergy_ids, dislike_ids);
  if (options?.hide_mandatory_allergy_items) {
    return filterMenuBuildersHidingMandatoryAllergyConflicts(enriched, allergy_ids);
  }
  return enriched;
}
